import { useLoaderData } from "@remix-run/react";
import { json, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import Confirmation from "~/shared/confirmation";

import "~/shared/confirmation.scss";
import "~/components/typography.scss";
import "~/components/button.scss";
import "~/components/logo.scss";
import { fetchJobPost } from "~/utils/fetch_job_post";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import useRecruitics from "~/hooks/use_recruitics";
import { withNamespace } from "~/utils/translation";
import useAppcast, { AppcastEvent } from "~/hooks/use_appcast";
import useSeekSignal from "~/hooks/use_seek_signal";

export const meta: MetaFunction = () => {
  const t = withNamespace("confirmation");
  return [{ title: t("page_title") }];
};

type Params = {
  url_token: string;
  job_post_id: string;
};

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { url_token: urlToken, job_post_id: jobPostId } = params as Params;
  const { jobPost, status } = await fetchJobPost({
    urlToken,
    jobPostId,
    apiVersion: "v2",
  });

  if (status === 404) {
    throw new Response(null, { status: 404 });
  }

  return json({
    internal: false,
    urlToken,
    jobPostId,
    jobPostPublicUrl: jobPost.public_url,
    companyName: jobPost.company_name,
    message: jobPost.confirmation_message,
    language: jobPost.language,
  });
};

export const handle = { i18n: ["confirmation", "job_post"] };

const ConfirmationPage = () => {
  const { internal, urlToken, jobPostId, jobPostPublicUrl, companyName, message } =
    useLoaderData<typeof loader>();
  const {
    button_shape: buttonShape,
    logo: logoConfiguration,
    enable_recruitics: enableRecruitics,
    appcast_tracking_code: appcastTrackingCode,
  } = useBoardConfiguration();

  useRecruitics(enableRecruitics);
  useAppcast(AppcastEvent.ApplyComplete, appcastTrackingCode);
  useSeekSignal();

  return (
    <Confirmation
      internal={internal}
      urlToken={urlToken}
      jobPostId={jobPostId}
      jobPostPublicUrl={jobPostPublicUrl}
      companyName={companyName}
      message={message}
      buttonShape={buttonShape}
      logoConfiguration={logoConfiguration}
    />
  );
};

export default ConfirmationPage;
